import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./MapPage.css";
import Drawer from "./Drawer";
import { FaPlus } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-jalaali";

moment.loadPersian({ dialect: "persian-modern", usePersianDigits: true });

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const FlyToMarker = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 16, { duration: 1.5 });
    }
  }, [position, map]);
  return null;
};

const MapPage = () => {
  const [markers, setMarkers] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFabOpen, setIsFabOpen] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const [notificationShown, setNotificationShown] = useState(true);
  const [initialFlyDone, setInitialFlyDone] = useState(false);

  const fetchDevices = async () => {
    try {
      const response = await fetch("/api/devices", {
        method: "GET",
        headers: { "accept": "application/json" },
        credentials: "include",
      });

      if (response.ok) {
        const devices = await response.json();

        const positions = await Promise.all(
          devices.map(async (device) => {
            try {
              const posResponse = await fetch(
                `/api/positions?deviceId=${device.id}`,
                {
                  method: "GET",
                  headers: { "accept": "application/json" },
                  credentials: "include",
                }
              );

              if (posResponse.ok) {
                const positionData = await posResponse.json();

                if (
                  Array.isArray(positionData) &&
                  positionData.length > 0 &&
                  positionData[0].latitude !== undefined &&
                  positionData[0].longitude !== undefined
                ) {
                  const pos = positionData[0];
                  const speedKmh = Math.round((pos.speed || 0) * 1.852);
                  const fixTime = pos.fixTime || pos.deviceTime || null;
                  const shamsiDate = fixTime ? moment(fixTime).format("jYYYY/jMM/jDD") : "—";
                  const shamsiTime = fixTime ? moment(fixTime).format("HH:mm:ss") : "—";

                  return {
                    id: device.id,
                    name: device.name,
                    position: [pos.latitude, pos.longitude],
                    speed: speedKmh,
                    shamsiDate,
                    shamsiTime,
                  };
                }
              }

              if (notificationShown) {
                toast.warn(`اطلاعات مارکر ${device.name} موجود نیست.`, {
                  position: "top-center",
                  autoClose: 3000,
                });
                setNotificationShown(false);
              }
              return null;
            } catch (error) {
              console.error(`❌ خطا در دریافت موقعیت دستگاه ${device.id}:`, error);
              return null;
            }
          })
        );

        setMarkers(positions.filter((pos) => pos !== null));
      } else if (response.status === 401) {
        toast.error("دسترسی نامعتبر، لطفاً دوباره وارد شوید.");
      } else {
        console.error("❌ خطا در دریافت دستگاه‌ها:", response.statusText);
      }
    } catch (error) {
      console.error("❌ خطا در دریافت دستگاه‌ها:", error);
    }
  };

  useEffect(() => {
    fetchDevices();
    const intervalId = setInterval(fetchDevices, 7000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationShown]);

  useEffect(() => {
    if (!initialFlyDone && markers.length > 0) {
      setFlyTarget(markers[0].position);
      setInitialFlyDone(true);
    }
  }, [markers, initialFlyDone]);

  return (
    <div className="map-page">
      <Drawer isOpen={isDrawerOpen} toggleDrawer={() => setIsDrawerOpen(!isDrawerOpen)} />

      <MapContainer center={[35.6892, 51.389]} zoom={13} className="map-container">
        <TileLayer
          url="https://custom-map-tile.torob.com/iran/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {markers.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
          >
            <Popup>
              <div className="marker-popup">
                <strong>{marker.name}</strong>
                <div className="popup-row">
                  <span className="popup-label">تاریخ:</span>
                  <span>{marker.shamsiDate}</span>
                </div>
                <div className="popup-row">
                  <span className="popup-label">ساعت:</span>
                  <span>{marker.shamsiTime}</span>
                </div>
                <div className="popup-row">
                  <span className="popup-label">سرعت:</span>
                  <span>{marker.speed} کیلومتر بر ساعت</span>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
        {flyTarget && <FlyToMarker position={flyTarget} />}
      </MapContainer>

      <div className={`fab-container ${isFabOpen ? "open" : ""}`}>
        <button className="fab-main" onClick={() => setIsFabOpen(!isFabOpen)}>
          <FaPlus />
        </button>
        {isFabOpen && (
          <div className="fab-items">
            {markers.map((marker) => (
              <button
                key={marker.id}
                className="fab-item"
                onClick={() => setFlyTarget([...marker.position])}
              >
                {marker.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default MapPage;
